import PropTypes from 'prop-types';
import {useEffect} from 'react';
import {createUseStyles} from 'react-jss';
import {connect} from 'react-redux';
import MainContentWrapper from '../../components/Accessibility/MainContentWrapper/MainContentWrapper';
import ProgressStep from '../../components/ProgressSteps/ProgressStep';
import MarketingPreferenceType from '../../enums/MarketingPreferenceType';
import ReCaptchaTypes from '../../enums/ReCaptchaTypes';
import Section from '../../enums/Sections';
import AdyenV2OptionsLoader from '../../listeners/AdyenV2OptionsLoader/AdyenV2OptionsLoader';
import AlipayPlusOptionsLoader from '../../listeners/AlipayPlusOptionsLoader/AlipayPlusOptionsLoader';
import AutoFillListener from '../../listeners/AutoFillListener';
import CreditListener from '../../listeners/CreditListener';
import getNewpayDetails from '../../listeners/NewpayOptionsLoader/getNewpayDetails';
import useNewpayWalletLoader from '../../listeners/NewpayOptionsLoader/useNewpayWalletLoader';
import OpttyOptionsLoader from '../../listeners/OpttyOptionsLoader/OpttyOptionsLoader';
import ZeroPaymentListener from '../../listeners/ZeroPaymentListener';
import RewardsPointsMessage from '../../sections/RewardsPointsMessage/RewardsPointsMessage';
import CheckoutSubmitSection from '../../sections/SubmitSection/CheckoutSubmitSection';
import MarketingPreferencesLazyLoader
  from '../../sections/SubmitSection/MarketingPreferences/MarketingPreferencesLazyLoader';
import RenderOn from '../../UI/RenderOn/RenderOn';
import Surface from '../../UI/Surface/Surface';
import {getDeliveryOptions} from '../../v2/actions/deliveryOptionActions';
import setNewpayScriptFailure from '../../v2/actions/paymentActions/newpay/setNewpayScriptFailure';
import AcknowledgedMessageLazyLoader from '../../v2/components/acknowledged-message/AcknowledgedMessageLazyLoader';
import ExpressCheckoutLazyLoader from '../../v2/components/ExpressCheckout/ExpressCheckout/ExpressCheckoutLazyLoader';
import OrderSummaryHeader from '../../v2/components/OrderSummary/OrderSummaryHeader/OrderSummaryHeader';
import OrderSummaryLazyLoader from '../../v2/components/OrderSummary/OrderSummaryLazyLoader';
import ProgressBar from '../../v2/components/ProgressBar/ProgressBar';
import ReturnToLoginLazyLoader from '../../v2/components/ReturnToLoginSection/ReturnToLoginLazyLoader';
import ThreeDSecureIdentify from '../../v2/components/ThreeDSecure/ThreeDSecureIdentify';
import PageMessage from '../../v2/error-message/PageMessage';
import {isMobile} from '../../v2/selectors/DeviceSelectors';
import getIsUsingExpressCheckout from '../../v2/selectors/ExpressSelectors/getIsUsingExpressCheckout';
import getHasLoginComponent from '../../v2/selectors/getHasLoginComponent';
import isAutofillListenerDisabled from '../../v2/selectors/getIsAutolistenerDisabled';
import getIsRecaptchaEnabled from '../../v2/selectors/getIsRecaptchaEnabled';
import getIsRecaptchaEnabledOrderSummary from '../../v2/selectors/getIsRecaptchaEnabledOrderSummary';
import getRecaptchaStrategy from '../../v2/selectors/getRecaptchaStrategy';
import getRecaptchaStrategyOrderSummary from '../../v2/selectors/getRecaptchaStrategyOrderSummary';
import {getMarketingPreferenceExperiment} from '../../v2/selectors/GuestSelectors';
import isLastStepSelector from '../../v2/selectors/isLastStepSelector';
import getIsAdyenV2Enabled from '../../v2/selectors/PaymentOptionSelectors/AdyenV2/getIsAdyenV2Enabled';
import isAlipayPlusAvailable from '../../v2/selectors/PaymentOptionSelectors/AlipayPlus/isAlipayPlusAvailable';
import getIsPondSelected from '../../v2/selectors/PaymentOptionSelectors/getIsPondSelected';
import isNewpayAvailable from '../../v2/selectors/PaymentOptionSelectors/Newpay/isNewpayAvailable';
import getIsOpttyAvailable from '../../v2/selectors/PaymentOptionSelectors/Optty/getIsOpttyAvailable';
import getIsRoktEnabled from '../../v2/selectors/RoktSelectors/getIsRoktEnabled';
import getIsRoktEnabledForCheckoutPage from '../../v2/selectors/RoktSelectors/getIsRoktEnabledForCheckoutPage';
import {getSettings, isZeroValueOrder} from '../../v2/selectors/selectors';
import shouldDisplaySubmitSection from '../../v2/selectors/shouldDisplaySubmitSectionSelector';
import MediaQueries from '../../v2/style-helper/MediaQueries';
import Spacing from '../../v2/style-helper/Spacing';
import DomUtils from '../../v2/utils/DomUtils';
import CheckoutPageSections from './CheckoutPageSections';
import RoktCheckoutBanner from './RoktCheckoutBanner';

const useStyles = createUseStyles({
  // empty classes needed so these already exist before being filled out within the media query
  checkout: {},
  clear: {},
  orderSummaryContainer: {},
  content: {
    paddingTop: Spacing.double,
  },
  notShowRecaptcha: {
    display: 'none',
  },
  [MediaQueries.tablet]: {
    checkout: {
      paddingRight: '1em',
      width: '66%',
    },
    clear: {
      clear: 'both',
    },
    orderSummaryContainer: {
      position: 'sticky',
      top: 0,
      float: 'right',
      width: '33%',
    },
    roktIframe: {
      border: 0,
      width: '100%',
      display: 'none',
    },
  },
  [MediaQueries.phone]: {
    content: {
      paddingTop: 0,
    },
  },
});

export function CheckoutPage(props) {
  const classes = useStyles(props);
  
  useNewpayWalletLoader(props.getNewpayDetails, props.setNewpayScriptFailure, props.isNewpayAvailable);
  
  useEffect(() => {
    if (!props.shouldDeferDeliveryOptionCallUntilPudoAvailabilityCallComplete) {
      props.getDeliveryOptions();
    }
    
    if (props.recaptchaEnabled || props.recaptchaEnabledPond) {
      if (props.invisibleRecaptchaEnabled || props.invisibleRecaptchaEnabledPond) {
        DomUtils.addScript('https://www.google.com/recaptcha/api.js');
      } else {
        DomUtils.addScript('https://www.google.com/recaptcha/api.js?onload=onloadCallback&render=explicit');
      }
    }
  }, [props.recaptchaEnabled, props.recaptchaEnabledPond, props.invisibleRecaptchaEnabled, props.invisibleRecaptchaEnabledPond, props.shouldDeferDeliveryOptionCallUntilPudoAvailabilityCallComplete]);
  
  const getAutoFillListener = () => {
    if (props.sections.some(sectionName => sectionName === Section.ADDRESS)) {
      if (props.isMobile && props.isAutofillListenerDisabled) {
        return null;
      }
      
      return <AutoFillListener />;
    }
  };
  
  return (
    <MainContentWrapper>
      <div data-test="CheckoutPage">
        {!props.isOneStepCheckout && <ProgressBar />}
        
        <form>
          <RenderOn mobile>
            <OrderSummaryHeader />
          </RenderOn>
          
          <div className={classes.content}>
            <RenderOn desktop>
              <div className={classes.orderSummaryContainer}>
                <Surface hasBackgroundColour>
                  <OrderSummaryLazyLoader />
                </Surface>
              </div>
            </RenderOn>
            
            <div className={classes.checkout}>
              <PageMessage />
              <RewardsPointsMessage />
              
              {props.showReturnToLogin && (
                <ProgressStep step={1}>
                  <ReturnToLoginLazyLoader />
                </ProgressStep>
              )}
              
              <ExpressCheckoutLazyLoader />
              
              {props.sections.map(sectionName => CheckoutPageSections[sectionName]).map((CheckoutSection, index) => (
                <>
                  {props.roktCheckoutEnabled && (
                    <RoktCheckoutBanner step={index + 1} />
                  )}
                  <ProgressStep surface step={index + 1}>
                    <CheckoutSection {...!props.isOneStepCheckout && {step: index + 1}} />
                  </ProgressStep>
                </>
              ))}
              
              <ProgressStep step={props.sections.length}>
                <AcknowledgedMessageLazyLoader />
              </ProgressStep>
              
              {props.usingExpressCheckout && props.showMarketingPreferenceContainer && (
                <ProgressStep step={props.sections.length}>
                  <MarketingPreferencesLazyLoader />
                </ProgressStep>
              )}
              
              {props.displaySubmitSection && <CheckoutSubmitSection />}
            
            </div>
            <div className={classes.clear} />
          </div>
        </form>
        
        <ThreeDSecureIdentify />
        <CreditListener />
        <ZeroPaymentListener />
        
        {getAutoFillListener()}
        
        {(props.isAdyenV2Enabled && !props.shouldLoadAdyenV2InPaymentOptionSection && !props.shouldDeferAdyenSessionLoadingUntilNonZeroValue) && (
          <AdyenV2OptionsLoader />
        )}
  
        {(props.isOpttyAvailable && !props.isZeroValueOrder) && (
          <OpttyOptionsLoader />
        )}
        
        {props.isAlipayPlusAvailable && (
          <AlipayPlusOptionsLoader />
        )}
      </div>
      
      {props.preLoadRoktAssets && (
        // eslint-disable-next-line jsx-a11y/iframe-has-title
        <iframe
          aria-hidden="true"
          src="https://apps.rokt.com/wsdk/preload/index.html"
          sandbox="allow-scripts allow-same-origin"
          className={classes.roktIframe}
        />
      )}
  
      <div className={
        props.invisibleRecaptchaEnabled
        || (props.invisibleRecaptchaEnabledPond && props.isPondSelected) ? '' : classes.notShowRecaptcha
}
      >
        <div
          id="recaptcha-container"
          className="g-recaptcha"
          data-sitekey={props.recaptchaSiteKey}
          data-callback="onSubmit"
          data-size="invisible"
        />
      </div>
    
    </MainContentWrapper>
  );
}

const mapStateToProps = state => ({
  sections: state.ui.sections,
  roktCheckoutEnabled: getIsRoktEnabledForCheckoutPage(state),
  isOneStepCheckout: state.ui.sections.length === 1,
  displaySubmitSection: shouldDisplaySubmitSection(state),
  usingExpressCheckout: getIsUsingExpressCheckout(state),
  showMarketingPreferenceContainer: (!getMarketingPreferenceExperiment(state)
    || getMarketingPreferenceExperiment(state) === 'c|standardMarketingPreferences')
    && state.email?.showMarketingPreferences !== MarketingPreferenceType.NONE,
  showReturnToLogin: getHasLoginComponent(state),
  preLoadRoktAssets: getIsRoktEnabled(state),
  recaptchaSiteKey: getSettings(state)?.recaptcha_token,
  recaptchaEnabledPond: getIsRecaptchaEnabledOrderSummary(state),
  recaptchaEnabled: getIsRecaptchaEnabled(state),
  invisibleRecaptchaEnabled: getRecaptchaStrategy(state) === ReCaptchaTypes.RECAPTCHA_V2_INVISIBLE,
  isPondSelected: getIsPondSelected(state),
  invisibleRecaptchaEnabledPond: getRecaptchaStrategyOrderSummary(state) === ReCaptchaTypes.RECAPTCHA_V2_INVISIBLE,
  isAdyenV2Enabled: getIsAdyenV2Enabled(state),
  isOpttyAvailable: getIsOpttyAvailable(state),
  isZeroValueOrder: isZeroValueOrder(state),
  shouldLoadAdyenV2InPaymentOptionSection: getSettings(state).should_load_adyen_v2_in_payment_option_section,
  isAlipayPlusAvailable: isAlipayPlusAvailable(state),
  isNewpayAvailable: isNewpayAvailable(state),
  isAutofillListenerDisabled: isAutofillListenerDisabled(state),
  isMobile: isMobile(state),
  shouldDeferDeliveryOptionCallUntilPudoAvailabilityCallComplete: !!getSettings(state).should_defer_delivery_option_call_until_pudo_availability_call_complete,
  shouldDeferAdyenSessionLoadingUntilNonZeroValue: isZeroValueOrder(state) && getSettings(state).should_defer_adyen_session_loading_until_non_zero_value,
  isLastStep: isLastStepSelector(state),
});

CheckoutPage.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.string).isRequired,
  roktCheckoutEnabled: PropTypes.bool.isRequired,
  getDeliveryOptions: PropTypes.func.isRequired,
  getNewpayDetails: PropTypes.func.isRequired,
  setNewpayScriptFailure: PropTypes.func.isRequired,
  isOneStepCheckout: PropTypes.bool.isRequired,
  displaySubmitSection: PropTypes.bool.isRequired,
  showMarketingPreferenceContainer: PropTypes.bool.isRequired,
  usingExpressCheckout: PropTypes.bool.isRequired,
  showReturnToLogin: PropTypes.bool.isRequired,
  preLoadRoktAssets: PropTypes.bool.isRequired,
  recaptchaEnabled: PropTypes.bool.isRequired,
  recaptchaEnabledPond: PropTypes.bool.isRequired,
  isPondSelected: PropTypes.bool.isRequired,
  invisibleRecaptchaEnabled: PropTypes.bool.isRequired,
  invisibleRecaptchaEnabledPond: PropTypes.bool.isRequired,
  isAlipayPlusAvailable: PropTypes.bool.isRequired,
  isNewpayAvailable: PropTypes.bool.isRequired,
  recaptchaSiteKey: PropTypes.string.isRequired,
  isAdyenV2Enabled: PropTypes.bool,
  isOpttyAvailable: PropTypes.bool,
  isZeroValueOrder: PropTypes.bool,
  shouldLoadAdyenV2InPaymentOptionSection: PropTypes.bool,
  isMobile: PropTypes.bool,
  isAutofillListenerDisabled: PropTypes.bool,
  shouldDeferDeliveryOptionCallUntilPudoAvailabilityCallComplete: PropTypes.bool,
  shouldDeferAdyenSessionLoadingUntilNonZeroValue: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps, {
  getDeliveryOptions,
  getNewpayDetails,
  setNewpayScriptFailure,
})(CheckoutPage);
